<div class="p-4 bg-slate-50 min-h-full">
  <!-- Page header -->
  <div class="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center border-b border-slate-200 pb-3 mb-5">
    <div class="flex items-center gap-3">
      <button type="button" (click)="goBack()" [title]="'hotline.back' | translate"
        class="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition">
        <mat-icon class="text-lg">arrow_back</mat-icon>
      </button>
      <h1 class="text-xl font-bold text-slate-900">{{ 'hotline.title' | translate }}</h1>
    </div>
    @if (parkingSpace(); as space) {
    <span
      class="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
      <mat-icon class="text-blue-600 text-base">local_parking</mat-icon>
      {{ space.name || ('hotline.parkingSpaceFallback' | translate) }}
    </span>
    }
  </div>

  <div class="grid grid-cols-1 gap-6" [class.xl:grid-cols-2]="!isRestrictedMember()">
    <!-- LEFT: Barrier Command Center -->
    <div class="bg-white border border-slate-100 rounded-xl shadow-sm p-4 flex flex-col space-y-4">
      <!-- Active overrides -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <h2 class="text-sm font-bold text-slate-800 flex items-center gap-2">
            <mat-icon class="text-blue-600 text-lg">sensor_door</mat-icon>
            {{ 'hotline.barrierCenter' | translate }}
          </h2>
        </div>

        <label class="text-xs font-medium text-slate-500 mb-1 block">{{ 'hotline.targetBarrier' | translate }}</label>
        @if (isLoadingBarriers()) {
        <div class="flex items-center gap-2 py-2 text-slate-400 text-sm">
          <mat-spinner diameter="16"></mat-spinner> {{ 'hotline.loadingBarriers' | translate }}
        </div>
        } @else {
        <select [ngModel]="selectedBarrierId()" (ngModelChange)="onBarrierChange($event)"
          [disabled]="barriers().length === 0"
          class="w-full border border-slate-200 rounded-lg py-1.5 px-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-slate-50 disabled:text-slate-400">
          @if (barriers().length === 0) {
          <option value="">{{ 'hotline.noBarriers' | translate }}</option>
          }
          @for (b of barriers(); track b.id) {
          <option [value]="b.id">{{ barrierLabel(b) }}</option>
          }
        </select>
        }

        <!-- Action button grid -->
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
          <button type="button" (click)="sendOpenTemporary()" [disabled]="isBusy() || !selectedBarrierId()"
            class="bg-blue-50 hover:bg-blue-100 border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed text-blue-700 font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition">
            @if (isOpeningTemporary()) {
            <mat-spinner diameter="18"></mat-spinner>
            } @else {
            <mat-icon class="text-lg">lock_clock</mat-icon>
            }
            {{ 'hotline.openTemporary' | translate }}
          </button>
          <button type="button" (click)="sendOpenPermanent()" [disabled]="isBusy() || !selectedBarrierId()"
            class="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition">
            @if (isOpeningPermanent()) {
            <mat-spinner diameter="18"></mat-spinner>
            } @else {
            <mat-icon class="text-lg">lock_open</mat-icon>
            }
            {{ 'hotline.openPermanent' | translate }}
          </button>
          <button type="button" (click)="sendClose()" [disabled]="isBusy() || !selectedBarrierId()"
            class="bg-slate-800 hover:bg-slate-950 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium py-2 px-3 rounded-lg flex items-center justify-center gap-2 transition">
            @if (isClosing()) {
            <mat-spinner diameter="18"></mat-spinner>
            } @else {
            <mat-icon class="text-lg">lock</mat-icon>
            }
            {{ 'hotline.close' | translate }}
          </button>
        </div>
      </div>

      <!-- Real-time override logs -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400">{{ 'hotline.liveStream' | translate }}
          </h3>
          <button type="button" (click)="loadLog()" [title]="'common.refresh' | translate"
            class="text-slate-400 hover:text-blue-600 transition flex items-center">
            <mat-icon class="text-base" [class.animate-spin]="isLoadingLog()">refresh</mat-icon>
          </button>
        </div>

        <div
          class="max-h-[350px] overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-lg log-scroll">
          @if (isLoadingLog() && logEntries().length === 0) {
          <div class="flex items-center gap-2 p-4 text-slate-400 text-sm">
            <mat-spinner diameter="16"></mat-spinner> {{ 'hotline.loadingActivity' | translate }}
          </div>
          } @else if (logEntries().length === 0) {
          <div class="p-6 text-center text-slate-400 text-sm">
            {{ 'hotline.noActivity' | translate }}
          </div>
          } @else {
          @for (entry of logEntries(); track entry.id) {
          <div class="flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50 transition">
            <div class="min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-sm font-medium text-slate-800 truncate">{{ entry.gateLabel }}</span>
                <span class="text-xs font-semibold shrink-0" [ngClass]="actionClass(entry.actionCode)">• {{ entry.action
                  }}</span>
              </div>
              <div class="text-slate-400 text-xs truncate flex items-center gap-1.5">
                <span>{{ entry.timestamp ? (entry.timestamp | date:'MMM d, HH:mm:ss') : '—' }}</span>
                @if (!isRestrictedMember()) {
                <span>·</span>
                <span class="font-mono font-semibold text-[11px] bg-slate-800 text-white px-1.5 py-0.5 rounded">{{
                  entry.licensePlate }}</span>
                }
              </div>
            </div>
            <span class="shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full"
              [ngClass]="entry.isManual ? 'bg-blue-50 text-blue-600' : 'bg-slate-100 text-slate-500'"
              [title]="'hotline.doneBy' | translate">
              {{ entry.triggerSource }}
            </span>
          </div>
          }
          }
        </div>
      </div>
    </div>

    <!-- RIGHT: Long-term supplier registration (hidden for restricted members) -->
    @if (!isRestrictedMember()) {
    <div class="bg-white border border-slate-100 rounded-xl shadow-sm p-4 flex flex-col space-y-4">
      <div>
        <h2 class="text-sm font-bold text-slate-800 flex items-center gap-2 mb-1">
          <mat-icon class="text-blue-600 text-lg">badge</mat-icon>
          {{ 'hotline.registerSupplier' | translate }}
        </h2>
        <p class="text-xs text-slate-400">
          @if (parkingSpace(); as space) {
          {{ 'hotline.authorizesFor' | translate:{ space: (space.name || ('hotline.parkingSpaceFallback' | translate)) }
          }}
          } @else {
          {{ 'hotline.credentialing' | translate }}
          }
        </p>
      </div>

      @if (!parkingSpace()) {
      <!-- No parking-space context -->
      <div
        class="flex-1 flex flex-col items-center justify-center text-center border-2 border-dashed border-slate-200 rounded-lg py-12 px-4">
        <div class="h-12 w-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
          <mat-icon class="text-slate-400">info</mat-icon>
        </div>
        <p class="text-sm font-medium text-slate-600">{{ 'hotline.selectSpaceFirst' | translate }}</p>
        <p class="text-xs text-slate-400 mt-1 max-w-xs">
          {{ 'hotline.selectSpaceHelp' | translate }}
        </p>
      </div>
      } @else {
      <!-- Which form: one long-term parker, or a company with its members (JKU-style) -->
      <div class="inline-flex self-start rounded-lg border border-slate-200 bg-slate-50 p-0.5 text-xs font-medium">
        <button type="button" (click)="registrationModeOverride.set('longTerm')" class="px-3 py-1.5 rounded-md transition"
          [ngClass]="registrationMode() === 'longTerm' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
          {{ 'hotline.mode.longTerm' | translate }}
        </button>
        <button type="button" (click)="registrationModeOverride.set('company')" class="px-3 py-1.5 rounded-md transition"
          [ngClass]="registrationMode() === 'company' ? 'bg-white text-blue-700 shadow-sm' : 'text-slate-500 hover:text-slate-700'">
          {{ 'hotline.mode.company' | translate }}
        </button>
      </div>

      @if (registrationMode() === 'longTerm') {
      <app-long-term-registration [parkingSpaceId]="parkingSpace()?.id ?? null"
        [operatorId]="currentOperator()?.id ?? null"
        [maxPlates]="parkingSpace()?.maxSelfServiceLicensePlates ?? null" (registered)="loadSupplierRegistry()">
      </app-long-term-registration>
      } @else {
      <!-- Company & members registration (suppliers + employees/students) -->
      <app-member-registration [parkingSpaceId]="parkingSpace()?.id ?? null"
        [parkingSpaceName]="parkingSpace()?.name ?? ''"
        [externalOrganizationId]="parkingSpace()?.externalOrganizationId ?? null" [zones]="availableZones()"
        [operatorId]="currentOperator()?.id ?? null" (registered)="loadSupplierRegistry()">
      </app-member-registration>
      }

      <!-- Active supplier registry -->
      <div>
        <!-- Header + filters on one row; they wrap to their own line on narrow screens. -->
        <div class="flex flex-wrap items-center gap-2 mb-2">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-400">{{ 'hotline.registry' | translate }}
          </h3>

          <!-- Search by license plate or supplier name. Typing searches the server after a short
               debounce (a plate can live outside the currently-loaded page); Enter or the search
               button skip the wait. -->
          <div class="relative flex-1 min-w-[12rem]">
            <button type="button" (click)="submitRegistrySearch()" [title]="'common.search' | translate"
              class="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition flex items-center">
              <mat-icon class="text-base">search</mat-icon>
            </button>
            <input type="text" [ngModel]="registrySearch()" (ngModelChange)="onRegistrySearch($event)"
              (keyup.enter)="submitRegistrySearch()"
              [placeholder]="'hotline.searchPlaceholder' | translate"
              class="w-full pl-9 pr-3 py-1.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>

          <button type="button" (click)="refreshRegistry()" [title]="'common.refresh' | translate"
            class="text-slate-400 hover:text-blue-600 transition flex items-center">
            <mat-icon class="text-base" [class.animate-spin]="isLoadingRegistry()">refresh</mat-icon>
          </button>
        </div>

        <!-- Sort controls + revoked visibility. "Show revoked" exists because a revoked company
             stayed in the database but vanished from this list, so it could be neither found nor
             recreated. -->
        <div class="flex flex-wrap items-center gap-1.5 mb-2">
          <span class="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mr-0.5">
            {{ 'hotline.sortBy' | translate }}
          </span>
          @for (col of sortableColumns; track col.key) {
          <button type="button" (click)="onSort(col.key)"
            class="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full border transition"
            [class]="sortKey() === col.key
              ? 'bg-slate-800 border-slate-800 text-white'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'">
            {{ col.labelKey | translate }}
            <mat-icon class="text-[13px] w-3.5 h-3.5 leading-none">{{ sortIcon(col.key) }}</mat-icon>
          </button>
          }

          <label
            class="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 cursor-pointer select-none">
            <input type="checkbox" [checked]="showRevoked()" (change)="onToggleRevoked()"
              class="w-3.5 h-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
            {{ 'hotline.showRevoked' | translate }}
          </label>
        </div>

        <!-- Classification chips. Rendered from the classifications this space actually uses, so a
             site without Students never shows a Student chip. Hidden entirely when nothing is
             classified yet (e.g. before the legacy backfill runs). -->
        @if (availableClassifications().length > 0) {
        <div class="flex flex-wrap items-center gap-1.5 mb-2">
          <button type="button" (click)="onClassificationFilter(allClassifications)"
            class="px-2.5 py-1 text-xs font-semibold rounded-full border transition" [class]="classificationFilter() === allClassifications
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'">
            {{ 'hotline.allClassifications' | translate }}
          </button>
          @for (c of availableClassifications(); track c) {
          <button type="button" (click)="onClassificationFilter(c)"
            class="px-2.5 py-1 text-xs font-semibold rounded-full border transition" [class]="classificationFilter() === c
              ? 'bg-blue-600 border-blue-600 text-white'
              : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'">
            {{ classificationLabelKey(c) | translate }}
          </button>
          }
        </div>
        }

        <div class="divide-y divide-slate-100 border border-slate-100 rounded-lg">
          @if (isLoadingRegistry() && supplierRegistry().length === 0) {
          <div class="flex items-center gap-2 p-4 text-slate-400 text-sm">
            <mat-spinner diameter="16"></mat-spinner> {{ 'hotline.loadingRegistry' | translate }}
          </div>
          } @else if (filteredRegistry().length === 0) {
          <div class="p-6 text-center text-slate-400 text-sm">
            {{ ((registrySearch() || classificationFilter() !== allClassifications)
            ? 'hotline.noSuppliersSearch' : 'hotline.noSuppliers') | translate }}
          </div>
          } @else {
          @for (entry of pagedRegistry(); track entry.id) {
          <div class="flex items-start justify-between gap-3 px-3 py-2.5 hover:bg-slate-50 transition">
            <div class="min-w-0 space-y-1">
              <div class="flex items-center gap-2 flex-wrap">
                <!--
                  The person leads, the company follows. The right is named after the company, so
                  showing that alone made every member of one company look like the same row —
                  which is exactly the report that a registered employee's name was nowhere visible.
                -->
                @if (entry.parkerName) {
                <span class="text-sm font-medium text-slate-800 truncate">{{ entry.parkerName }}</span>
                <span class="text-xs text-slate-400 truncate">·</span>
                <span class="text-xs text-slate-500 truncate">{{ entry.supplierName }}</span>
                } @else {
                <span class="text-sm font-medium text-slate-800 truncate">{{ entry.supplierName }}</span>
                }
                @for (p of entry.plates; track p) {
                <span
                  class="shrink-0 font-mono text-[11px] font-semibold bg-slate-800 text-white px-1.5 py-0.5 rounded">{{
                  p }}</span>
                }
                @if (entry.isRevoked) {
                <span class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">
                  {{ 'hotline.revoked' | translate }}
                </span>
                } @else {
                <span class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                  [ngClass]="entry.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'">
                  {{ (entry.isActive ? 'common.active' : 'common.inactive') | translate }}
                </span>
                }
                @if (entry.akNumber) {
                <span
                  class="shrink-0 font-mono text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700">
                  {{ entry.akNumber }}
                </span>
                }
                <!-- Classification badge — sourced from ParkerSiteClassification, never parsed from the comment. -->
                @if (entry.classification !== null) {
                <span class="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-700">
                  {{ classificationLabelKey(entry.classification) | translate }}
                </span>
                }
              </div>
              <!-- Parking-right details -->
              <div class="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-slate-500 text-xs">
                <span class="inline-flex items-center gap-1">
                  <mat-icon class="text-[13px] w-3.5 h-3.5 leading-none text-slate-400">event</mat-icon>
                  @if (entry.validFrom) { {{ 'hotline.from' | translate }} {{ entry.validFrom | date:'MMM d, y' }} }
                  @if (entry.validTo) { {{ 'hotline.until' | translate }} {{ entry.validTo | date:'MMM d, y' }} } @else
                  {
                  {{ 'hotline.noExpiry' | translate }} }
                </span>
                <span class="inline-flex items-center gap-1">
                  <mat-icon class="text-[13px] w-3.5 h-3.5 leading-none text-slate-400">location_on</mat-icon>
                  {{ entry.zonesLabel }}
                </span>
                <span class="inline-flex items-center gap-1">
                  <mat-icon class="text-[13px] w-3.5 h-3.5 leading-none text-slate-400">badge</mat-icon>
                  {{ entry.operatorLabel }}
                </span>
              </div>
              @if (entry.comment) {
              <p class="text-xs text-slate-400 italic truncate">“{{ entry.comment }}”</p>
              }
            </div>
            <!-- Revoked rights stay read-only (EditParkingRight rejects a deleted right), but they
                 can now be restored — revoking used to be one-way and needed support to undo. -->
            <div class="shrink-0 flex items-center gap-1 mt-0.5">
              @if (entry.isRevoked) {
              <button type="button" (click)="restoreSupplier(entry)" [disabled]="restoringId() === entry.id"
                [title]="'hotline.restore' | translate"
                class="text-slate-400 hover:text-emerald-600 disabled:opacity-50 transition flex items-center">
                @if (restoringId() === entry.id) {
                <mat-spinner diameter="16"></mat-spinner>
                } @else {
                <mat-icon class="text-lg">restore_from_trash</mat-icon>
                }
              </button>
              } @else {
              <button type="button" (click)="editSupplier(entry)" [title]="'hotline.edit.action' | translate"
                class="text-slate-400 hover:text-blue-600 transition flex items-center">
                <mat-icon class="text-lg">edit</mat-icon>
              </button>
              <button type="button" (click)="revokeSupplier(entry)" [disabled]="revokingId() === entry.id"
                [title]="'hotline.revoke' | translate"
                class="text-slate-400 hover:text-red-600 disabled:opacity-50 transition flex items-center">
                @if (revokingId() === entry.id) {
                <mat-spinner diameter="16"></mat-spinner>
                } @else {
                <mat-icon class="text-lg">block</mat-icon>
                }
              </button>
              }
            </div>
          </div>
          }
          }
        </div>

        <!-- Pagination -->
        @if (filteredRegistry().length > registryPageSize) {
        <div class="flex items-center justify-between mt-2 text-xs text-slate-500">
          <span>{{ 'hotline.pageInfo' | translate:{ current: registryPageIndex() + 1, total: registryTotalPages(),
            count: filteredRegistry().length } }}</span>
          <div class="flex items-center gap-1">
            <button type="button" (click)="registryPrevPage()" [disabled]="registryPageIndex() === 0"
              class="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              <mat-icon class="text-base">chevron_left</mat-icon>
            </button>
            <button type="button" (click)="registryNextPage()"
              [disabled]="registryPageIndex() >= registryTotalPages() - 1"
              class="inline-flex items-center justify-center w-7 h-7 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition">
              <mat-icon class="text-base">chevron_right</mat-icon>
            </button>
          </div>
        </div>
        }
      </div>
      }
    </div>
    }
  </div>
</div>